import { isWebView, isNativeAppWebView } from 'utils/webview';
import isAccountWebTransport from 'utils/accountWebTransport';

/**
 * Unified Reserhub account enablement policy.
 *
 * Deliberately free of service imports: `services/growthBook` pulls in the Redux store and the whole
 * reducer tree, which components that only need this policy (e.g. `StoredCardsCheckout`) must not
 * carry in their import graph. GrowthBook values are passed in by the caller instead.
 * `utils/isReserhubUnifiedAccountEnabled` re-exports everything here and adds the GrowthBook reader.
 */

/** Whitelabel feature that opts a tenant into the unified account inside its native app WebView. */
export const NATIVE_APP_ACCOUNT_FEATURE = 'RESERHUB_ACCOUNT_ON_NATIVE_APP_ENABLED';
export const CDS_ACCOUNT_WEBVIEW_FEATURE = 'CDS_ACCOUNT_WEBVIEW_ENABLED';

/**
 * Whether `env.reserhub.account` turns on unified Reserhub account behavior.
 * Requires an object account with truthy `loginUrl` and `homeUrl`; `enabled: false` disables.
 * @param {object|null|undefined} account - Whitelabel `env.reserhub.account`.
 * @param {object} [options] - unifiedAccountFeature (GrowthBook).
 * @param {boolean|string|object|undefined} [options.unifiedAccountFeature] - GrowthBook `unified_account_{brand}` value.
 * @returns {boolean}
 */
export default function isReserhubUnifiedAccountEnabled(account, options = {}) {
  const { unifiedAccountFeature } = options;

  if (!account || typeof account !== 'object') return false;
  if (account.enabled === false || !account.loginUrl || !account.homeUrl) {
    return false;
  }

  // If account is enabled and does not use progressive rollout, unified account is enabled.
  if (!account.progressiveRollout) {
    return true;
  }

  // If account uses progressive rollout, unified account is enabled only if GrowthBook feature is truthy.
  if (account.progressiveRollout === true && !unifiedAccountFeature) {
    return false;
  }

  return true;
}

/**
 * Whether unified Reserhub account behavior applies to the client currently running the funnel.
 *
 * Layers the WebView gate on top of the tenant-level check in `isReserhubUnifiedAccountEnabled`.
 * A marked CDS Account WebView opts in via `CDS_ACCOUNT_WEBVIEW_ENABLED`; other native
 * clients keep their existing `RESERHUB_ACCOUNT_ON_NATIVE_APP_ENABLED` gate.
 *
 * The native-app check runs before `isWebView()` on purpose. `isWebView()` is a user-agent heuristic
 * and a native app whose UA does not trip it must still require the flag — reversing the order would
 * leave an unflagged path into the very apps the flag exists to exclude.
 *
 * The `isWebView()` veto below is inherited unchanged from `!isWebView() && …` on `develop`, and is
 * preserved deliberately: lifting it would enable the unified account for third-party in-app
 * browsers on every tenant that configures `env.reserhub.account`, with no flag behind it. The
 * population that veto used to catch by mistake — Chrome, Firefox and Edge on iOS, which emit no
 * `Version/` — is excluded in `isWebView()` itself, so those visitors now resolve as plain web
 * rather than being vetoed here.
 *
 * Takes `account` and `features` as arguments rather than reading the store, so the loyalty widget
 * handler (which is not a hook) and the account provider can share one definition.
 *
 * @param {object|null|undefined} account - Whitelabel `env.reserhub.account`.
 * @param {object} [options] - Enablement inputs.
 * @param {boolean|string|object|undefined} [options.unifiedAccountFeature] - GrowthBook `unified_account` value.
 * @param {object} [options.features] - Whitelabel `features` map.
 * @param {string} [options.brand] - Resolved whitelabel brand.
 * @returns {boolean}
 */
export function isUnifiedAccountEnabledForClient(account, options = {}) {
  const { unifiedAccountFeature, features, brand } = options;

  if (isNativeAppWebView()) {
    if (isAccountWebTransport(brand)) {
      if (features?.[CDS_ACCOUNT_WEBVIEW_FEATURE] !== true) return false;
    } else if (!features?.[NATIVE_APP_ACCOUNT_FEATURE]) return false;
    return isReserhubUnifiedAccountEnabled(account, { unifiedAccountFeature });
  }

  // Status quo from `develop`; see the JSDoc — widening this is a separate, unflagged change.
  if (isWebView()) return false;

  return isReserhubUnifiedAccountEnabled(account, { unifiedAccountFeature });
}
